/**
 * FamilyMemberForm Component
 *
 * Reusable form for creating or editing a family member.
 * Used by both the create and edit pages.
 */

import { useState, useEffect } from 'react';
import {
  Alert,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';
import type { CreateFamilyMemberInput, UpdateFamilyMemberInput } from '@/types/family-member';
import { NotesLinkRow } from './NotesLinkRow';

type FormValues = {
  name: string;
  dateOfBirth: string;
  notes: string;
};

type FamilyMemberFormProps = {
  initialValues: FormValues;
  mode: 'create' | 'edit';
  isLoading: boolean;
  canDelete?: boolean;
  onSubmit: (values: CreateFamilyMemberInput | UpdateFamilyMemberInput) => Promise<void>;
  onDelete?: () => void;
  onEditNotes?: () => void;
  notesCount?: number;
};

export function FamilyMemberForm({
  initialValues,
  mode,
  isLoading,
  canDelete = false,
  onSubmit,
  onDelete,
  onEditNotes,
  notesCount,
}: FamilyMemberFormProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  const [name, setName] = useState(initialValues.name);
  const [dateOfBirth, setDateOfBirth] = useState(initialValues.dateOfBirth);
  const [notes, setNotes] = useState(initialValues.notes);

  // Sync with initialValues when they change (e.g., after loading)
  useEffect(() => {
    setName(initialValues.name);
    setDateOfBirth(initialValues.dateOfBirth);
    setNotes(initialValues.notes);
  }, [initialValues.name, initialValues.dateOfBirth, initialValues.notes]);

  const handleSubmit = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      Alert.alert('Name Required', 'Please enter a name for this family member.');
      return;
    }

    const parsedNotes = notes
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);

    const input: CreateFamilyMemberInput | UpdateFamilyMemberInput = {
      name: trimmedName,
      dateOfBirth: dateOfBirth.trim() || null,
      ...(onEditNotes
        ? {}
        : { notes: parsedNotes.length > 0 ? parsedNotes : null }),
    };
    await onSubmit(input);
  };

  const handleDelete = () => {
    if (!onDelete) return;

    Alert.alert(
      'Delete Family Member',
      'Are you sure you want to delete this family member? This will also remove them from all calendar mappings.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: onDelete,
        },
      ]
    );
  };

  return (
    <KeyboardAwareScrollView
      style={styles.scrollView}
      contentContainerStyle={styles.scrollContent}
      keyboardShouldPersistTaps="handled"
      enableOnAndroid={true}
      extraScrollHeight={50}
      enableAutomaticScroll={true}
    >
      {/* Name Input */}
      <View style={styles.inputGroup}>
        <Text style={[styles.label, { color: palette.textMuted, fontFamily: getBodyFont() }]}>
          Name
        </Text>
        <TextInput
          style={[
            styles.textInput,
            {
              backgroundColor: palette.cardBackground,
              color: palette.text,
              borderColor: palette.border,
              fontFamily: getBodyFont(),
            },
          ]}
          value={name}
          onChangeText={setName}
          placeholder="Enter name"
          placeholderTextColor={palette.textMuted}
          autoFocus={mode === 'create'}
          editable={!isLoading}
        />
      </View>

      {/* Date of Birth Input */}
      <View style={styles.inputGroup}>
        <Text style={[styles.label, { color: palette.textMuted, fontFamily: getBodyFont() }]}>
          Date of Birth (optional)
        </Text>
        <TextInput
          style={[
            styles.textInput,
            {
              backgroundColor: palette.cardBackground,
              color: palette.text,
              borderColor: palette.border,
              fontFamily: getBodyFont(),
            },
          ]}
          value={dateOfBirth}
          onChangeText={setDateOfBirth}
          placeholder="YYYY-MM-DD"
          placeholderTextColor={palette.textMuted}
          keyboardType="numbers-and-punctuation"
          editable={!isLoading}
        />
      </View>

      {/* Notes */}
      {onEditNotes ? (
        <View style={styles.inputGroup}>
          <NotesLinkRow notesCount={notesCount ?? 0} onPress={onEditNotes} />
        </View>
      ) : (
        <View style={styles.inputGroup}>
          <Text style={[styles.label, { color: palette.textMuted, fontFamily: getBodyFont() }]}>
            Notes (optional, for AI context — one per line)
          </Text>
          <TextInput
            style={[
              styles.textInput,
              styles.textArea,
              {
                backgroundColor: palette.cardBackground,
                color: palette.text,
                borderColor: palette.border,
                fontFamily: getBodyFont(),
              },
            ]}
            value={notes}
            onChangeText={setNotes}
            placeholder={'e.g.\nLoves soccer\nAllergic to peanuts'}
            placeholderTextColor={palette.textMuted}
            multiline
            numberOfLines={4}
            textAlignVertical="top"
            editable={!isLoading}
          />
        </View>
      )}

      {/* Submit Button */}
      <TouchableOpacity
        style={[styles.submitButton, { backgroundColor: palette.primary }]}
        onPress={handleSubmit}
        disabled={isLoading}
      >
        <Text style={[styles.submitButtonText, { fontFamily: getBodyFont() }]}>
          {isLoading ? 'Saving...' : mode === 'create' ? 'Create Member' : 'Save Changes'}
        </Text>
      </TouchableOpacity>

      {/* Delete Button */}
      {mode === 'edit' && canDelete && onDelete && (
        <TouchableOpacity
          style={[styles.deleteButton, { borderColor: palette.danger }]}
          onPress={handleDelete}
          disabled={isLoading}
        >
          <Ionicons name="trash-outline" size={18} color={palette.danger} />
          <Text style={[styles.deleteButtonText, { color: palette.danger }]}>
            Delete Family Member
          </Text>
        </TouchableOpacity>
      )}
    </KeyboardAwareScrollView>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: Spacing.lg,
    paddingBottom: 48, // intentionally large for scroll clearance
  },
  inputGroup: {
    marginBottom: Spacing.lg,
  },
  label: {
    fontSize: 13,
    marginBottom: Spacing.sm,
  },
  textInput: {
    borderWidth: 1,
    borderRadius: BorderRadius.sm + 2, // 10px
    paddingHorizontal: Spacing.md + 2, // 14px
    paddingVertical: Spacing.md,
    fontSize: 16,
  },
  textArea: {
    minHeight: 100,
    paddingTop: Spacing.md,
  },
  submitButton: {
    paddingVertical: Spacing.md + 2, // 14px
    borderRadius: BorderRadius.sm + 2, // 10px
    alignItems: 'center',
    marginTop: Spacing.sm,
  },
  submitButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  deleteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: Spacing.sm,
    paddingVertical: Spacing.md + 2, // 14px
    borderWidth: 1,
    borderRadius: BorderRadius.sm + 2, // 10px
    marginTop: Spacing.lg,
  },
  deleteButtonText: {
    fontSize: 16,
    fontWeight: '500',
  },
});
